/**
 * Reads a MotionPlan at a timestamp: per-node pop/wave scale, the camera
 * glide and nudge, per-edge draw-in / colour / shimmer, the arrival flash
 *. Pure; the board calls it once per frame.
 */
import { mixHex } from "../../theme/colorScience";
import { MOTION, type MotionFrame, type MotionPlan } from "./motion";
import { releaseAt, waveScale } from "./wave";

export { waveCurve } from "./wave";
import type { EdgeMotion } from "./BoardEdges";

const easeOut = (t: number) => 1 - Math.pow(1 - t, 3);
const clamp01 = (t: number) => Math.max(0, Math.min(1, t));

/** Keyframes [offset, value], eased out within each segment. */
function frames(t: number, kf: [number, number][]): number {
  if (t <= 0) return kf[0][1];
  if (t >= 1) return kf[kf.length - 1][1];
  for (let i = 1; i < kf.length; i++) {
    const [o1, v1] = kf[i];
    const [o0, v0] = kf[i - 1];
    if (t <= o1) return v0 + (v1 - v0) * easeOut((t - o0) / (o1 - o0));
  }
  return kf[kf.length - 1][1];
}

const popCurve = (t: number, peak: number) =>
  frames(t, [
    [0, 1],
    [0.38, peak],
    [0.72, 0.95],
    [1, 1],
  ]);
const IDLE: MotionFrame = {
  active: false,
  nodeScale: () => 1,
  camera: { k: 1, dx: 0, dy: 0 },
  edge: () => undefined,
  flash: null,
  optionScale: () => 1,
  ghosts: [],
  targetFill: null,
};

/** A new option's pop-in: from nothing to a small peak, then rest. */
function optionPop(t: number) {
  return frames(t, [
    [0, 0],
    [0.38, MOTION.optionPeak],
    [0.72, 0.95],
    [1, 1],
  ]);
}

export function sampleMotion(
  plan: MotionPlan | null,
  now: number,
): MotionFrame {
  if (!plan || now >= plan.end) return IDLE;
  const el = Math.max(0, now - plan.t0);
  const { crunch, glide, wave, win, shimmer, cool } = plan;

  const nodeScale = (id: string) => {
    let s = 1;
    if (crunch?.pop && crunch.node === id) {
      s *= popCurve((el - MOTION.popDelay) / MOTION.popMs, 1.35);
    }
    if (wave) s *= waveScale(wave, id, el);
    if (win) s *= waveScale(win, id, el);
    return s;
  };

  const camera = { k: 1, dx: 0, dy: 0 };
  if (glide && el < MOTION.glideMs) {
    const e = easeOut(clamp01(el / MOTION.glideMs));
    camera.k = glide.k0 + (1 - glide.k0) * e;
    camera.dx = glide.dx0 * (1 - e);
    camera.dy = glide.dy0 * (1 - e);
  }
  if (crunch && el < MOTION.nudgeMs) {
    const n =
      MOTION.nudgePx *
      frames(el / MOTION.nudgeMs, [
        [0, 0],
        [0.35, 1],
        [0.7, -0.3],
        [1, 0],
      ]);
    camera.dx += crunch.dir.x * n;
    camera.dy += crunch.dir.y * n;
  }

  const edge = (key: string): EdgeMotion | undefined => {
    const m: EdgeMotion = {};
    if (crunch?.edgeKey === key && el < MOTION.drawMs) {
      m.draw = easeOut(clamp01(el / MOTION.drawMs));
    }
    const c = cool?.edges.find((e) => e.key === key);
    if (cool && c && el < MOTION.coolMs) {
      const t = clamp01(el / MOTION.coolMs);
      m.color =
        t < 0.45
          ? mixHex(c.fromColor, MOTION.coolGrey, easeOut(t / 0.45))
          : mixHex(MOTION.coolGrey, cool.to, easeOut((t - 0.45) / 0.55));
    }
    const wi = win ? win.edges.findIndex((e) => e.key === key) : -1;
    if (win && wi >= 0) {
      const t = (el - win.delay - win.starts[wi + 1]) / MOTION.winEdgeMs;
      m.color = mixHex(win.edges[wi].from, win.gold, easeOut(clamp01(t)));
    }
    const shimmerEnd =
      MOTION.drawMs + MOTION.shimmerLoopMs * MOTION.shimmerLoops;
    if (
      shimmer?.edges.includes(key) &&
      el >= MOTION.drawMs &&
      el < shimmerEnd
    ) {
      const loop =
        ((el - MOTION.drawMs) % MOTION.shimmerLoopMs) / MOTION.shimmerLoopMs;
      m.shimmerOffset = -MOTION.shimmerDash * loop;
    }
    return Object.keys(m).length ? m : undefined;
  };

  let flash: MotionFrame["flash"] = null;
  const fe = el - MOTION.flashAt;
  if (crunch && fe >= 0 && fe < 2 * MOTION.flashFrameMs) {
    flash = {
      node: crunch.node,
      opacity: fe < MOTION.flashFrameMs ? 0.95 : 0.475,
    };
  }

  const born = plan.options?.born ?? [];
  const optionScale = (id: string) => {
    const i = born.indexOf(id);
    if (i < 0) return 1;
    const t =
      (el - MOTION.optionInDelay - i * MOTION.optionGap) / MOTION.optionPopMs;
    return t <= 0 ? 0 : optionPop(t);
  };
  const fade = 1 - el / MOTION.optionFadeMs;
  const ghosts =
    plan.options && fade > 0
      ? plan.options.ghosts.map((g) => ({ ...g, opacity: fade }))
      : [];

  const targetFill = win
    ? {
        node: win.nodes[win.nodes.length - 1],
        level: clamp01((el - releaseAt(win)) / Math.max(1, MOTION.winFillMs)),
      }
    : null;

  return {
    active: true,
    targetFill,
    nodeScale,
    camera,
    edge,
    flash,
    optionScale,
    ghosts,
  };
}
